import { ReactNode } from 'react'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import { useVisualPlan } from 'app/frontend/base/config/queries'
import {
  VisualPlanData,
  VisualPlanScreen,
  VisualRaidPlan,
} from '../components/TeamPlanAssignmentVisualCard'
import { RaidstratsPlayer } from '../components/RaidstratsContainer'
import useVisualPlanLiveSync from '../components/visual_plans/hooks/useVisualPlanLiveSync'
import { resolveVisualPlanEntityAssignments } from '../components/visual_plans/shapes/entityAssignmentMatching'
import {
  getActiveVisualScreens,
  getScopeScreens,
} from '../components/visualScreenScopes'
import { getRaidPlanLinkInfo } from '../components/raidPlanLink'
import {
  AssignmentViewMode,
  getEffectiveAssignmentViewMode,
} from '../components/assignmentTimeline'
import {
  AssignmentPhaseTimestampBadgeData,
  buildAssignmentPhaseTimestampBadgeData,
  PhaseDivider,
} from '../components/AssignmentPhaseTimestampBadge'

type SpecializationInfo = {
  name: string
  icon: string
  classId: number
  className: string
}

type Options = {
  owner: RaidPlanOwner
  ability: RaidPlanEncounterAssignmentAbility
  scopes: { id: string; castGroups: number[][] }[]
  entitiesByScopeId: Record<string, RaidPlanAssignmentEntity[]>
  entries: { time: number; phaseIndex: number }[]
  phaseDividers: PhaseDivider[]
  specializationById: Record<number, SpecializationInfo>
  memberInfoByCharacterId: Record<number, { name: string; className: string }>
  raidPlanPlayers: RaidstratsPlayer[]
  ownCharacterIds?: Set<number>
  overriddenEntityIds?: Set<string>
  getEntityLabel?: (entity: RaidPlanAssignmentEntity) => ReactNode
}

const useAbilityVisualPlanData = ({
  owner,
  ability,
  scopes,
  entitiesByScopeId,
  entries,
  phaseDividers,
  specializationById,
  memberInfoByCharacterId,
  raidPlanPlayers,
  ownCharacterIds,
  overriddenEntityIds,
  getEntityLabel,
}: Options): {
  visualPlan: VisualPlanData | null
  visualRaidPlan: VisualRaidPlan | null
} => {
  const { data: linkedVisual } = useVisualPlan(owner, ability.visual_id)
  useVisualPlanLiveSync({ owner, visualId: ability.visual_id })
  const abilityViewMode: AssignmentViewMode = getEffectiveAssignmentViewMode(
    ability.view_mode,
    entries.length,
  )
  const activeVisualScreens = linkedVisual
    ? getActiveVisualScreens(linkedVisual.screens, ability.visual_screen_ids)
    : []
  const scopeFillByScreenId = new Map<
    string,
    { scopeId: string; entities: RaidPlanAssignmentEntity[] }
  >()
  const screenCastBadges = new Map<
    string,
    AssignmentPhaseTimestampBadgeData[]
  >()
  scopes.forEach((scope, scopeIndex) => {
    const flatScopeEntities = entitiesByScopeId[scope.id] ?? []
    const scopeScreens = getScopeScreens({
      viewMode: abilityViewMode,
      scopeId: scope.id,
      scopeIndex,
      activeScreens: activeVisualScreens,
    })
    scopeScreens.forEach(({ screen }) => {
      if (!scopeFillByScreenId.has(screen.id)) {
        scopeFillByScreenId.set(screen.id, {
          scopeId: scope.id,
          entities: flatScopeEntities,
        })
      }
    })
    if (scopeScreens.length > 0) {
      scope.castGroups.forEach((castGroup, groupIndex) => {
        const numbered = scopeScreens[groupIndex % scopeScreens.length]
        if (!numbered) return
        const badge = buildAssignmentPhaseTimestampBadgeData({
          castIndexes: castGroup,
          entries,
          phaseDividers,
        })
        const existing = screenCastBadges.get(numbered.screen.id) ?? []
        existing.push(badge)
        screenCastBadges.set(numbered.screen.id, existing)
      })
    }
  })
  const combinedVisualScreens: VisualPlanScreen[] = activeVisualScreens.flatMap(
    ({ screen, number }) => {
      const fill = scopeFillByScreenId.get(screen.id)
      if (!fill) return []
      const entityAssignments = resolveVisualPlanEntityAssignments({
        shapes: screen.shapes,
        entities: fill.entities,
        specializationById,
        memberInfoByCharacterId,
      })
      entityAssignments.forEach((badge) => {
        const entity = fill.entities[badge.index]
        if (!entity) return
        if (getEntityLabel) badge.label = getEntityLabel(entity)
        if (entity.id && overriddenEntityIds?.has(entity.id)) {
          badge.isOverridden = true
        }
        if (
          ownCharacterIds &&
          entity.kind === 'member' &&
          ownCharacterIds.has(entity.character_id)
        ) {
          badge.isOwn = true
        }
      })
      return [
        {
          key: `${fill.scopeId}:${screen.id}`,
          scopeId: fill.scopeId,
          screen,
          number,
          entityAssignments,
          castTimestamps: screenCastBadges.get(screen.id) ?? [],
        },
      ]
    },
  )
  const visualPlan: VisualPlanData | null =
    linkedVisual && combinedVisualScreens.length > 0
      ? {
          encounterId: linkedVisual.encounterId,
          screens: combinedVisualScreens,
        }
      : null
  const linkInfo = getRaidPlanLinkInfo(ability.raid_plan_link)
  const visualRaidPlan: VisualRaidPlan | null =
    linkInfo.hasValidRaidPlanId && linkInfo.extractedRaidPlanId
      ? {
          raidPlanId: linkInfo.extractedRaidPlanId,
          players: raidPlanPlayers,
        }
      : null
  return { visualPlan, visualRaidPlan }
}

export default useAbilityVisualPlanData
